/**
 * Job status types
 */
export type JobStatus = 'Sold' | 'Closed' | 'NoGo';

/**
 * Main Job entity from backend API
 */
export interface Job {
  id: number;
  leadId: number;
  jobNumber: string;
  status: JobStatus;
  startDate: string;
  completedDate?: string;
  closedDate?: string;
  description?: string;
  notes?: string;
  closingComments?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Job list item DTO (for search results)
 */
export interface JobListItemDto {
  id: number;
  leadId: number;
  jobNumber: string;
  status: JobStatus;
  startDate: string;
  customerName: string;
  leadTitle: string;
  propertyAddress: string;
  assignedTo?: string;
  totalCharges: number;
  totalCosts: number;
  profit: number;
}

/**
 * Job detail DTO (full job with lead and financial info)
 */
export interface JobDetailDto {
  id: number;
  leadId: number;
  jobNumber: string;
  status: JobStatus;
  startDate: string;
  completedDate?: string;
  closedDate?: string;
  description?: string;
  notes?: string;
  closingComments?: string;
  createdAt: string;
  updatedAt: string;
  // Estimator/commission fields (authoritative, from Job entity)
  assignedTo?: string;
  assignedToUserId?: number;
  commissionPercent?: number;
  subcontractorId?: number;
  subcontractorName?: string;
  subcontractorCommissionRate?: number;
  payrollUserId?: number;
  payrollCommissionRate?: number;
  lead: LeadSummaryDto;
  financial: JobFinancialSummaryDto;
}

/**
 * Lead summary within job detail (customer/property info only)
 */
export interface LeadSummaryDto {
  id: number;
  customerName: string;
  propertyAddress: string;
  insuranceCompany?: string;
  claimNumber?: string;
}

/**
 * Financial summary for a job
 */
export interface JobFinancialSummaryDto {
  totalCharges: number;
  totalCosts: number;
  totalTax: number;
  totalPaymentsReceived: number;
  totalPaymentsPending: number;
  profit: number;
  profitPercent: number;
  customerBalance: number;
  jobBalance: number;
}

/**
 * Command to create a new job from an existing lead
 */
export interface CreateJobFromLeadCommand {
  propertyAddress1?: string;
  propertyAddress2?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  assignedToUserId?: number;
  subcontractorId?: number;
  payrollUserId?: number;
  description?: string;
}

/**
 * Form data for updating a job
 */
export interface JobFormData {
  description?: string;
  notes?: string;
  assignedToUserId?: number | null;
  commissionRate?: number | null;
  subcontractorId?: number | null;
  subcontractorCommissionRate?: number | null;
  payrollUserId?: number | null;
  payrollCommissionRate?: number | null;
}

/**
 * Job status update DTO
 */
export interface JobStatusUpdateDto {
  newStatus: JobStatus;
  closingComments?: string;
  completedDate?: string;
  closedDate?: string;
}

/**
 * Query parameters for searching jobs
 */
export interface JobsQueryParams {
  status?: JobStatus;
  startDateFrom?: string;
  startDateTo?: string;
  assignedToId?: number;
  leadId?: number;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * API Response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Search jobs response with pagination
 */
export interface SearchJobsResponse {
  items: JobListItemDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/**
 * Commission breakdown for a job
 */
export interface JobCommissionBreakdownDto {
  jobId: number;
  jobNumber: string;
  totalCharges: number;
  totalCosts: number;
  profit: number;
  profitPercent: number;
  commissions: CommissionDetailDto[];
  totalCommission: number;
}

/**
 * Commission type discriminators emitted by the API.
 *
 * These MUST stay in sync with the literals in JobsService.GetJobCostOverview
 * (atlas-api/src/Atlas.API/Features/Jobs/JobsService.cs). They are matched by
 * string equality to decide which sidebar panel to render, so a rename on
 * either side silently hides a panel rather than failing loudly — which is
 * exactly what happened when "Estimator" was renamed to "Project Manager" and
 * the consumer was not updated, leaving the estimator summary invisible.
 * Referencing these constants instead of inline strings keeps the two sides
 * findable together.
 */
export const CommissionTypes = {
  ProjectManager: 'Project Manager',
  Subcontractor: 'Subcontractor',
  Payroll: 'Payroll',
} as const;

export type CommissionType = (typeof CommissionTypes)[keyof typeof CommissionTypes];

/**
 * Individual commission detail
 */
export interface CommissionDetailDto {
  personId: number | null;
  personName: string;
  commissionType: string;
  commissionRate: number;
  commissionAmount: number;
  paidToDate: number;
  amountDue: number;
}
